import axios from 'axios';

const APP_VERSION = process.env.REACT_APP_VERSION || '0.0.0';

export const SESSION_ID_KEY = 'cdd_session_id';
export const RELOAD_META_KEY = 'cdd_reload_meta';
export const LOGIN_FAIL_KEY = 'cdd_login_fail';

const MAX_BREADCRUMBS = 20;
const MAX_REPORTS_PER_MINUTE = 40;
const RELOAD_LOOP_WINDOW_MS = 60 * 1000;
const RELOAD_LOOP_THRESHOLD = 3;
const LOGIN_FAIL_THRESHOLD = 3;

const breadcrumbs = [];
let reportTimestamps = [];
let knownUser = { userId: null, email: null };

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `cdd-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function storageGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // private mode
  }
}

export function getSessionId() {
  let id = storageGet(SESSION_ID_KEY);
  if (!id) {
    id = randomId();
    storageSet(SESSION_ID_KEY, id);
  }
  return id;
}

export function newRequestId() {
  return randomId();
}

export function setTracedUser({ userId, email } = {}) {
  knownUser = {
    userId: userId || null,
    email: email ? String(email).toLowerCase().trim() : null,
  };
}

export function clearTracedUser() {
  knownUser = { userId: null, email: null };
}

export function addBreadcrumb(event, extra = {}) {
  breadcrumbs.push({
    t: new Date().toISOString(),
    event,
    ...extra,
  });
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

function allowReport() {
  const now = Date.now();
  reportTimestamps = reportTimestamps.filter((ts) => now - ts < 60_000);
  if (reportTimestamps.length >= MAX_REPORTS_PER_MINUTE) {
    return false;
  }
  reportTimestamps.push(now);
  return true;
}

export function attachTraceHeaders(headers = {}) {
  const next = headers && typeof headers === 'object' ? headers : {};
  next['X-Request-Id'] = next['X-Request-Id'] || newRequestId();
  next['X-Session-Id'] = getSessionId();
  next['X-App-Version'] = APP_VERSION;
  return next;
}

/**
 * Send a structured diagnostic event to POST /logger (CloudWatch front group).
 * Never throws. Does not send passwords.
 */
export async function reportSessionEvent(event, extra = {}) {
  addBreadcrumb(event, { path: extra.path, status: extra.status, reason: extra.reason });

  if (!allowReport()) {
    return;
  }

  const API_KEY = window?.env?.REACT_APP_LOGGER_API_KEY || process.env.REACT_APP_LOGGER_API_KEY;
  const payload = {
    kind: 'cdd.trace',
    event,
    level: extra.level || 'info',
    time: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    sessionId: getSessionId(),
    appVersion: APP_VERSION,
    userId: extra.userId || knownUser.userId,
    email: extra.email || knownUser.email,
    path: extra.path,
    status: extra.status,
    reason: extra.reason,
    message: extra.message,
    stack: extra.stack,
    context: extra.context,
    count: extra.count,
    breadcrumbs: breadcrumbs.slice(-MAX_BREADCRUMBS),
  };

  try {
    await axios.post('/logger', payload, {
      headers: {
        'x-api-key': API_KEY,
        'Content-Type': 'application/json',
      },
      skipAuthRefresh: true,
      skipConnectivityGuard: true,
      skipSessionTrace: true,
    });
  } catch {
    // diagnostics must never break the app
  }
}

export function noteLoginFailure(email) {
  const now = Date.now();
  let state = { email: email || '', count: 0, firstAt: now };
  try {
    const raw = storageGet(LOGIN_FAIL_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.email === (email || '') && now - parsed.firstAt < 15 * 60 * 1000) {
        state = parsed;
      }
    }
  } catch {
    // ignore
  }
  state.count += 1;
  storageSet(LOGIN_FAIL_KEY, JSON.stringify(state));
  if (state.count >= LOGIN_FAIL_THRESHOLD) {
    void reportSessionEvent('session.unhealthy', {
      level: 'error',
      email,
      reason: 'repeated_login_failure',
      count: state.count,
    });
  }
  return state.count;
}

export function clearLoginFailures() {
  try {
    sessionStorage.removeItem(LOGIN_FAIL_KEY);
  } catch {
    // ignore
  }
}

/**
 * Returns false when a version/chunk reload would loop — caller must not navigate.
 */
export function shouldReloadForNewVersion() {
  const now = Date.now();
  let meta = { count: 0, firstAt: now };
  try {
    const raw = storageGet(RELOAD_META_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (now - parsed.firstAt < RELOAD_LOOP_WINDOW_MS) {
        meta = parsed;
      }
    }
  } catch {
    // ignore
  }

  meta.count += 1;
  storageSet(RELOAD_META_KEY, JSON.stringify(meta));

  if (meta.count >= RELOAD_LOOP_THRESHOLD) {
    void reportSessionEvent('ui.reload_loop_blocked', {
      level: 'error',
      reason: 'version_or_chunk_reload',
      count: meta.count,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
    return false;
  }

  void reportSessionEvent('ui.reload_scheduled', {
    level: 'warn',
    count: meta.count,
    path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
  return true;
}

export function startSessionTrace() {
  getSessionId();
  void reportSessionEvent('ui.session.start', {
    path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}
